import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  collection,
  addDoc,
  getDocs,
  query as firestoreQuery,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useSelector, useDispatch } from 'react-redux';
import { setCustomers } from '../../redux/slices/customersSlice';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';

function CustomerList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const dispatch = useDispatch();
  const { customers = [] } = useSelector((state) => state.customers || {});
  const { query: searchQuery } = useSelector((state) => state.search || {});
  const { openCustomerDialog, setOpenCustomerDialog } = useOutletContext();
  const [isLoading, setIsLoading] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    businessType: '',
    website: '',
  });

  // Fetch customers only when component mounts or when customers array is empty
  useEffect(() => {
    if (customers.length === 0 && !isLoading) {
      fetchCustomers();
    }
  }, [customers.length, isLoading]);

  // Memoize filtered customers based on search query
  const displayedCustomers = useMemo(() => {
    if (!searchQuery) return customers;

    return customers.filter(
      (customer) =>
        customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.businessId
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        customer.id?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [customers, searchQuery]);

  const fetchCustomers = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const q = firestoreQuery(collection(db, 'customers'), orderBy('name'));
      const querySnapshot = await getDocs(q);
      const customersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      dispatch(setCustomers(customersData));
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCustomer = async () => {
    try {
      await addDoc(collection(db, 'customers'), {
        ...newCustomer,
        createdAt: new Date(),
        createdBy: currentUser.uid,
        isActive: true,
      });
      setOpenCustomerDialog(false);
      setNewCustomer({
        name: '',
        email: '',
        phone: '',
        address: '',
        businessType: '',
        website: '',
      });
      fetchCustomers();
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  if (isLoading && customers.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Grid container sx={{ width: '100%', margin: 0 }}>
        {displayedCustomers.map((customer) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{ width: '25%', padding: '12px' }}
            key={customer.id}
          >
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                width: '100%',
                '&:hover': {
                  boxShadow: 6,
                },
              }}
              onClick={() => navigate(`/customers/${customer.id}`)}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'left',
                    mb: 0,
                  }}
                >
                  <div className="d-flex">
                    <CardMedia
                      component="img"
                      sx={{
                        width: 50,
                        height: 50,
                        objectFit: 'contain',
                        borderRadius: 8,
                        backgroundColor: 'background.paper',
                        mb: 2,
                      }}
                      image={`https://storage.googleapis.com/business-logos-node/${customer.id}.png`}
                      alt={`${customer.name} logo`}
                    />
                    <div className="mx-4">
                      <Typography
                        variant="h6"
                        align="left"
                        gutterBottom
                        sx={{
                          width: '200px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {customer.name}
                      </Typography>
                      <Chip
                        label={customer.isActive ? t('active') : t('inactive')}
                        color={customer.isActive ? 'success' : 'error'}
                        size="small"
                        sx={{ fontSize: 10 }}
                      />
                    </div>
                  </div>
                </Box>
                <Divider sx={{ my: 1 }} component="div" />
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('customers.businessType')}
                  </Typography>
                  <Typography variant="body1">
                    {customer.businessType || t('customers.notSpecified')}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('customers.contact')}
                  </Typography>
                  <Typography variant="body1">
                    {customer.email || t('customers.notSpecified')}
                  </Typography>
                  <Typography variant="body1">
                    {customer.phone || t('customers.notSpecified')}
                  </Typography>
                </Box>
                {customer.website && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('customers.website')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {customer.website}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {displayedCustomers.length === 0 && !isLoading && (
          <Grid item xs={12}>
            <Typography align="center" color="text.secondary">
              {t('customers.noCustomersFound')}
            </Typography>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={openCustomerDialog}
        onClose={() => setOpenCustomerDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('customers.createCustomer')}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t('customers.name')}
            fullWidth
            value={newCustomer.name}
            onChange={(e) =>
              setNewCustomer({ ...newCustomer, name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('customers.email')}
            fullWidth
            value={newCustomer.email}
            onChange={(e) =>
              setNewCustomer({ ...newCustomer, email: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('customers.phone')}
            fullWidth
            value={newCustomer.phone}
            onChange={(e) =>
              setNewCustomer({ ...newCustomer, phone: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('customers.address')}
            fullWidth
            multiline
            rows={2}
            value={newCustomer.address}
            onChange={(e) =>
              setNewCustomer({ ...newCustomer, address: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('customers.businessType')}
            fullWidth
            value={newCustomer.businessType}
            onChange={(e) =>
              setNewCustomer({ ...newCustomer, businessType: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('customers.website')}
            fullWidth
            value={newCustomer.website}
            onChange={(e) =>
              setNewCustomer({ ...newCustomer, website: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCustomerDialog(false)}>
            {t('customers.cancel')}
          </Button>
          <Button
            onClick={handleCreateCustomer}
            variant="contained"
            disabled={!newCustomer.name}
          >
            {t('customers.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomerList;
